import { JoinSessionModel } from "@/models/join-session.model";
import { UpdateLessonAndUnitModel } from "@/models/update-lesson-and-unit.model";
import {
  CheckSessionStartResp,
  FetchOngoingSessionModel,
  FetchTeamsReqModel,
  FetchTeamsResModel,
  PostLocalUserReconnectedReqModel,
  PutRemoteUserDisconnectedReqModel,
  PutTeamsReqModel,
  PutTeamsResModel,
  PutToggleTeamModeReqModel,
  PutToggleTeamModeResModel,
  RemoteTeachingServiceInterface,
  TeacherUpdateCanvasObjectRequestModel,
  TeacherUpdateMediaStateRequestModel,
  mapSessionOneToOneForTeacher,
} from "@/services";
import { GLServiceBase } from "vue-glcommonui";
import {
  GetMinimalSessionDtoResponse,
  NextSessionResponse,
  SessionReplacementResponse,
  SessionStateResponse,
  FetchSessionDtoResModel,
  UnitAndLessonResponse,
  ToggleStudentPaletteReq,
  ToggleStudentPalettesResp,
  SetStudentsBadgeResp,
  ClearStudentRaisingHandResp,
  SetStudentsBadgeReq,
  BaseResponse,
  FetchVideoOnTeacherDisconnect,
  RealtimeTokenResponse,
  TeacherUpdateAnnotationModeRequestModel,
  HelperJoinSessionModel,
} from "./model";
import { UpdateExposureModel, ToggleTargetsModel, UpdateIndependentExposureItemModel, EndExposureModel, HelperToggleTeacherAudioModel } from "@/ws";
import { AnnotationMode, RoomModel, TeachingMode, UnitAndLessonModel } from "@/models/room.model";
import { store } from "@/store";
import { vuexName, MODULES } from "@/store/utils";
import { ZoomSlidePositionModel, ZoomSlideModel } from "@/hooks/use-remote-requests.";
import { getDeviceId } from "@/utils/utils";
import { isSensitiveDataRestricted } from "@/utils/tenant";
import { FetchContentSignatureRespModel } from "./interface";
class GLRemoteTeachingService extends GLServiceBase<any, any> implements RemoteTeachingServiceInterface {
  serviceRoute = { prefix: "connect/v1" };
  get userRequestModel() {
    return {
      userId: store.getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)]?.profile?.sub,
      sessionId: store.getters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)],
      studentId: store.getters[vuexName(MODULES.STUDENT_ROOM.GETTERS.GET_STUDENT)]?.id,
      isTeacherUseOnly: store.getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_USE_ONLY)],
      isTeacherOneOne: !!store.getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)],
      resolution: isSensitiveDataRestricted() ? undefined : `${screen.width * window.devicePixelRatio}x${screen.height * window.devicePixelRatio}`,
      deviceId: getDeviceId(),
    };
  }

  fetchContentSignature(): Promise<FetchContentSignatureRespModel> {
    return this.get("content/signature");
  }

  getRemoteLessons(
    versionGroupId: string,
    unit: number,
    maxUnit?: number | null,
    remoteLessonPlanVersionId?: string | null,
  ): Promise<UnitAndLessonModel[]> {
    return this.get(`content/lessons/version/${versionGroupId}/unit/${unit}`, {
      maxUnit,
      remoteLessonPlanVersionId,
    });
  }

  fetchSessionDataAsTeacher(deviceId: string): Promise<FetchSessionDtoResModel> {
    return this.get("teacher/online-session", { deviceId }).then((res) => mapSessionOneToOneForTeacher(res as FetchSessionDtoResModel));
  }

  teacherStartClassRoom(startModel: JoinSessionModel): Promise<FetchSessionDtoResModel> {
    return this.create("teacher/join-session", { ...startModel });
  }

  teacherEndClassRoom(roomId?: string, markAsComplete?: boolean): Promise<any> {
    if (!roomId) return Promise.resolve(null);
    return this.update("teacher/end-class/" + roomId + `?MarkAsComplete=${markAsComplete?.toString()}`);
  }

  studentGetRoomInfo(childId: string, deviceId: string): Promise<FetchSessionDtoResModel> {
    this.serviceRoute = { prefix: "connect/v1" };
    const { resolution } = this.userRequestModel;
    const promise = this.get(`student/online-session`, {
      studentId: childId,
      resolution,
      deviceId,
    });
    return promise as Promise<FetchSessionDtoResModel>;
  }

  checkSessionStart(studentId: string, deviceId: string): Promise<CheckSessionStartResp> {
    const promise = this.get(`student/is-online-session`, {
      studentId,
      deviceId,
    });
    return promise as Promise<CheckSessionStartResp>;
  }

  acceptPolicy(role: string): Promise<any> {
    return this.get(`policy/${role}/is-accepted`);
  }

  submitPolicy(role: string): Promise<any> {
    return this.create(`policy/${role}/accept/true`);
  }

  fetchVideoOnTeacherDisconnect(unitId: number, lessonId: number): Promise<FetchVideoOnTeacherDisconnect> {
    return this.get(`student/video-on-teacher-disconnect/unit/${unitId}/lesson/${lessonId}`);
  }

  raisingHand(raisingHand: boolean): Promise<any> {
    const { studentId, sessionId } = this.userRequestModel;
    return this.create(`student/raise-hand`, {
      raisingHand,
      studentId,
      sessionId,
    });
  }

  studentLeaveSession(studentId: string, sessionId: string): Promise<void> {
    return this.create(`student/${studentId}/sessions/${sessionId}/leave`);
  }

  getListLessonByUnit(classId: string, groupId: string, unit: number): Promise<UnitAndLessonResponse> {
    return this.get(`lesson-plan/sequence/class/${classId}/group/${groupId}/unit/${unit}`);
  }

  getStudentNextSession(listIds: string[]): Promise<Array<NextSessionResponse>> {
    let studentId = "";
    listIds.map((id: string, index: number) => {
      if (index != 0) {
        studentId += "&";
      }
      studentId += "studentId=" + id;
    });
    return this.get(`student/next-session?${studentId}&`);
  }
  getMinimalSessionDto(sessionId: string, studentId?: string): Promise<GetMinimalSessionDtoResponse> {
    return this.get(`session/${sessionId}/minimal?studentId=${studentId}&`);
  }

  fetchRealtimeToken(userId?: string): Promise<RealtimeTokenResponse> {
    return this.get(`realtime/token?clientId=${userId}&`) as Promise<RealtimeTokenResponse>;
  }

  teacherUpdateLessonAndUnit(model: UpdateLessonAndUnitModel): Promise<RoomModel> {
    return this.update("session/update-lesson-and-unit", model);
  }

  fetchSessionDtoById(sessionId: string): Promise<FetchSessionDtoResModel> {
    return this.get(`session/${sessionId}`);
  }

  sessionReplacementAccept4Teacher(deviceId: string): Promise<SessionReplacementResponse> {
    return this.update(`teacher/update-new-device?deviceId=${deviceId}`);
  }
  sessionReplacementAccept4Helper(deviceId: string): Promise<SessionReplacementResponse> {
    return this.update(`helper/update-new-device?deviceId=${deviceId}`);
  }
  getSessionState(sessionId: string): Promise<SessionStateResponse> {
    return this.get(`session/media-state/${sessionId}`);
  }
  teacherUpdateMediaState(model: TeacherUpdateMediaStateRequestModel): Promise<boolean> {
    return this.update(`teacher/media-state`, model, undefined, { activeRequestKey: "TeacherUpdateMediaState" });
  }
  teacherSetOneOne(sessionId: string, studentId?: string | null) {
    const url = studentId ? `teacher/one-one/${sessionId}/${studentId}` : `teacher/one-one/${sessionId}`;
    return this.create(url);
  }
  teacherSetWhiteboard(isShowWhiteBoard: boolean): Promise<boolean> {
    const { sessionId, isTeacherOneOne } = this.userRequestModel;
    return this.create("teacher/whiteboard", { sessionId, isShow: isShowWhiteBoard, isOneOne: isTeacherOneOne });
  }
  //** Informing to the SERVER that remote user is disconnected */
  putRemoteUserDisconnected(params: PutRemoteUserDisconnectedReqModel) {
    const { localUserId, sessionId, remoteUserId, remoteUserRole } = params;
    return this.update(`${remoteUserRole}/on-disconnect`, { sessionId, userDisconnectedId: remoteUserId, userRequestId: localUserId });
  }
  postLocalUserReconnected(params: PostLocalUserReconnectedReqModel) {
    const { localUserId, sessionId, localUserRole } = params;
    return this.create(`${localUserRole}/reconnect?sessionId=${sessionId}&userId=${localUserId}`);
  }
  fetchTeams(params: FetchTeamsReqModel): Promise<FetchTeamsResModel> {
    return this.get(`team/session/${params.sessionId}`);
  }
  putToggleTeamMode(params: PutToggleTeamModeReqModel): Promise<PutToggleTeamModeResModel> {
    const { isOpen, sessionId } = params;
    return this.update(`team/session/${sessionId}/toggle/${isOpen}`);
  }
  putTeams(params: PutTeamsReqModel): Promise<PutTeamsResModel> {
    const { teams, sessionId, revision } = params;
    return this.update(`team/session/${sessionId}/upsert?revision=${revision}`, teams);
  }
  updateCanvasObject(params: TeacherUpdateCanvasObjectRequestModel): Promise<boolean> {
    return this.create(`draw/teacher/canvas-object`, params);
  }
  toggleStudentPalette(params: ToggleStudentPaletteReq): Promise<ToggleStudentPalettesResp> {
    return this.create(`teacher/toggle-student-palette`, params);
  }
  setStudentsBadge(params: SetStudentsBadgeReq): Promise<SetStudentsBadgeResp> {
    return this.create(`teacher/set-badge`, params);
  }
  clearStudentRaisingHand(sessionId: string, studentId: string): Promise<ClearStudentRaisingHandResp> {
    return this.create(`teacher/clear-raising-hand/${studentId}/session/${sessionId}`);
  }
  toggleTargets(params: ToggleTargetsModel): Promise<BaseResponse<boolean>> {
    return this.create(`draw/toggle-targets`, params);
  }
  fetchOngoingSession(): Promise<BaseResponse<FetchOngoingSessionModel | null>> {
    return this.get("teacher/online-sessions/participation");
  }
  setLessonAction(sessionId: string, action: number, isHelper?: boolean): Promise<any> {
    return this.create("teacher/set-lesson-action", { sessionId, action, isHelper });
  }
  updateExposure(params: UpdateExposureModel): Promise<any> {
    return this.update(`teacher/update-exposure`, params);
  }
  endExposure(params: EndExposureModel): Promise<any> {
    return this.update(`teacher/end-exposure`, params);
  }
  updateIndependentExposureItem(params: UpdateIndependentExposureItemModel): Promise<any> {
    return this.update(`student/update-independent-exposure-item`, params);
  }
  setLessonPlanCollapse(sessionId: string, mode: TeachingMode): Promise<any> {
    return this.update(`teacher/set-lesson-plan-collapse`, { sessionId, mode });
  }
  teacherUpdateAnnotationMode(mode: AnnotationMode): Promise<boolean> {
    const { sessionId, isTeacherUseOnly, isTeacherOneOne } = this.userRequestModel;
    if (!sessionId) return Promise.resolve(false);
    const params: TeacherUpdateAnnotationModeRequestModel = {
      mode,
      sessionId,
      isOneOne: isTeacherOneOne,
      isTeacherUseOnly,
    };
    return this.update(`teacher/set-mode`, params);
  }
  teacherScrollPdf(ratio: number): Promise<boolean> {
    const { sessionId, isTeacherUseOnly, isTeacherOneOne } = this.userRequestModel;
    const params = {
      ratio,
      sessionId,
      isOneOne: isTeacherOneOne,
      isTeacherUseOnly,
    };
    return this.update(`teacher/scroll-pdf`, params);
  }
  teacherZoomSlide(payload: ZoomSlideModel): Promise<boolean> {
    const { sessionId, isTeacherUseOnly, isTeacherOneOne } = this.userRequestModel;
    const params = {
      ...payload,
      sessionId,
      isOneOne: isTeacherOneOne,
      isTeacherUseOnly,
    };
    return this.update(`teacher/zoom-slide`, params);
  }
  teacherMoveZoomedSlide(payload: ZoomSlidePositionModel): Promise<boolean> {
    const { sessionId, isTeacherUseOnly, isTeacherOneOne } = this.userRequestModel;
    const params = {
      ...payload,
      sessionId,
      isOneOne: isTeacherOneOne,
      isTeacherUseOnly,
    };
    return this.update(`teacher/move-zoomed-slide`, params);
  }
  helperToggleTeacherAudio(params: HelperToggleTeacherAudioModel): Promise<boolean> {
    return this.update(`helper/toggle-teacher-audio`, params);
  }
  helperEnterClass(): Promise<boolean> {
    const { sessionId, resolution, userId, deviceId } = this.userRequestModel;
    const params: HelperJoinSessionModel = {
      sessionId,
      deviceId,
      resolution,
      teacherId: userId,
    };
    return this.create(`helper/enter-class`, params);
  }
  teacherEnterClass(): Promise<boolean> {
    const { sessionId, resolution, userId, deviceId } = this.userRequestModel;
    const params: HelperJoinSessionModel = {
      sessionId,
      deviceId,
      resolution,
      teacherId: userId,
    };
    return this.create(`teacher/enter-class`, params);
  }
  studentEnterClass(): Promise<boolean> {
    const { sessionId, studentId, resolution, deviceId } = this.userRequestModel;
    return this.create(`student/enter-class`, {
      sessionId,
      studentId,
      resolution,
      deviceId,
    });
  }
}

export const RemoteTeachingService = new GLRemoteTeachingService();
